import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Lightbulb } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

export const getOrderSentencesFromContent = (content) => {
  if (Array.isArray(content?.sentences)) {
    return content.sentences.filter((s) => (s || '').toString().trim().length > 0);
  }
  return [];
};

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const rand = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(rand(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Shuffle but never leave the array in its original order.
const shuffleTilesWithSeed = (tiles, seed) => {
  if (!tiles || tiles.length < 2) return [...(tiles || [])];
  let seedValue = seed;
  let result = shuffleWithSeed(tiles, seedValue);
  let attempts = 0;
  while (
    attempts < 8 &&
    result.every((t, i) => t.originalIndex === i)
  ) {
    seedValue += 100;
    result = shuffleWithSeed(tiles, seedValue);
    attempts++;
  }
  return result;
};

const OrderSentencesElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentArrangement = null,
  showResult = false,
  perItemResults = null,
  isCorrect = null,
  onArrangementChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    hint = '',
    fontSize = 16,
    fontFamily = 'Inter',
    tileColor = '#ffffff',
    tileTextColor = '#1f2937',
    tileBorderColor = '#2A4D25',
    actionBarColor = '#f9fafb',
    alignment = 'center',
    showNumbers = true,
    numberColor,
  } = content || {};
  const hasCustomNumberColor = typeof numberColor === 'string' && numberColor.length > 0;
  const numberStyleColor = hasCustomNumberColor ? numberColor : tileTextColor;
  const numberStyleOpacity = hasCustomNumberColor ? 1 : 0.7;
  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';
  const alignItemsClass = alignment === 'left'
    ? 'items-start'
    : alignment === 'right'
      ? 'items-end'
      : 'items-stretch';

  const sentences = useMemo(() => getOrderSentencesFromContent(content), [content]);

  const scrambledTiles = useMemo(() => {
    if (sentences.length === 0) return [];
    const tiles = sentences.map((text, idx) => ({
      id: `${id || 'order_sentences'}_s${idx}`,
      text,
      originalIndex: idx,
    }));
    const baseSeed = id
      ? id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
      : 1;
    return shuffleTilesWithSeed(tiles, baseSeed);
  }, [sentences, id]);

  // Keep incoming arrangement in sync with the current sentences. If a sentence
  // was added/removed in editor, drop stale IDs and fall back to the scramble.
  const normalizedExternal = useMemo(() => {
    if (!Array.isArray(currentArrangement) || currentArrangement.length === 0) return null;
    if (currentArrangement.length !== scrambledTiles.length) return null;
    const scrambledIds = new Set(scrambledTiles.map((t) => t.id));
    if (!currentArrangement.every((t) => t && scrambledIds.has(t.id))) return null;
    return currentArrangement;
  }, [currentArrangement, scrambledTiles]);

  const [tiles, setTiles] = useState(normalizedExternal || scrambledTiles);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const pointerDragRef = useRef(null);
  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: sentences.length,
    onAutoGrow,
  });

  useEffect(() => {
    if (normalizedExternal) {
      setTiles(normalizedExternal);
    } else if (scrambledTiles.length > 0) {
      setTiles(scrambledTiles);
    } else {
      setTiles([]);
    }
  }, [normalizedExternal, scrambledTiles]);

  const dragEnabled = !disabled && !showResult && !isEditor;

  const moveTile = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setTiles((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      onArrangementChange?.(next);
      return next;
    });
  }, [onArrangementChange]);

  const handleDragStart = (e, index) => {
    if (!dragEnabled) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${index}`);

    // Safari's default drag ghost snapshots a scaled-down slice of the page,
    // which looks like a tiny slide thumbnail. Replace it with a clean clone
    // of the tile so only the sentence is visible while dragging.
    const el = e.currentTarget;
    if (el && e.dataTransfer.setDragImage) {
      const rect = el.getBoundingClientRect();
      const clone = el.cloneNode(true);
      Object.assign(clone.style, {
        position: 'fixed',
        top: '-10000px',
        left: '-10000px',
        width: `${rect.width}px`,
        boxSizing: 'border-box',
        pointerEvents: 'none',
        opacity: '0.95',
        boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      });
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top);
      setTimeout(() => {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      }, 0);
    }
  };

  const handleDragOver = (e, index) => {
    if (!dragEnabled) return;
    if (dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragIndex) setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverIndex(null);
  };

  const handleDrop = (e, index) => {
    if (!dragEnabled) return;
    e.preventDefault();
    if (dragIndex !== null) moveTile(dragIndex, index);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Pointer drag (touch/pen) — HTML5 drag doesn't fire on mobile browsers.
  const handlePointerDown = useCallback((e, index) => {
    if (!dragEnabled) return;
    if (e.pointerType === 'mouse') return;
    if (pointerDragRef.current) return;
    const tile = tiles[index];
    if (!tile) return;
    e.preventDefault();

    const ghost = document.createElement('div');
    ghost.textContent = tile.text;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      padding: '8px 14px',
      borderRadius: '10px',
      border: `2px solid ${tileBorderColor}`,
      background: tileColor,
      color: tileTextColor,
      font: `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`,
      maxWidth: '80vw',
      pointerEvents: 'none',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      zIndex: '99999',
      opacity: '0.95',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      willChange: 'transform',
    });
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

    pointerDragRef.current = { fromIndex: index, ghostEl: ghost, pointerId: e.pointerId };
    setDragIndex(index);
  }, [dragEnabled, tiles, tileBorderColor, tileColor, tileTextColor, fontFamily, fontSize]);

  useEffect(() => {
    const findIndexAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const tileEl = el && el.closest ? el.closest('[data-order-sentences-index]') : null;
      if (!tileEl) return null;
      const idx = Number(tileEl.getAttribute('data-order-sentences-index'));
      return Number.isNaN(idx) ? null : idx;
    };

    const cleanup = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDragIndex(null);
      setDragOverIndex(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      const idx = findIndexAt(e.clientX, e.clientY);
      if (idx !== null && idx !== drag.fromIndex) {
        setDragOverIndex(idx);
      } else {
        setDragOverIndex(null);
      }
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const idx = findIndexAt(e.clientX, e.clientY);
      const fromIndex = drag.fromIndex;
      cleanup();
      if (idx !== null && idx !== fromIndex) {
        moveTile(fromIndex, idx);
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanup();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [moveTile]);

  useEffect(() => () => {
    const drag = pointerDragRef.current;
    if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
      drag.ghostEl.parentNode.removeChild(drag.ghostEl);
    }
    pointerDragRef.current = null;
  }, []);

  const handleCheck = () => onCheck?.(tiles);

  const canCheck = sentences.length > 0 && tiles.length === sentences.length;

  const tileCorrectness = (index) => {
    if (!showResult) return null;
    if (perItemResults && perItemResults[index] != null) return perItemResults[index];
    const tile = tiles[index];
    return tile ? tile.originalIndex === index : null;
  };

  const renderTileRow = (tile, index, interactive) => {
    const correctness = interactive ? tileCorrectness(index) : null;
    let bg = tileColor;
    let color = tileTextColor;
    let borderColor = tileBorderColor;
    if (showResult && interactive) {
      if (correctness === true) {
        bg = '#dcfce7'; color = '#166534'; borderColor = '#22c55e';
      } else if (correctness === false) {
        bg = '#fee2e2'; color = '#991b1b'; borderColor = '#ef4444';
      }
    }
    const isDragging = interactive && dragIndex === index;
    const isDragOver = interactive
      && dragOverIndex === index
      && dragIndex !== index;
    return (
      <div
        key={tile.id}
        data-order-sentences-index={interactive && dragEnabled ? index : undefined}
        draggable={interactive && dragEnabled}
        onDragStart={interactive ? (e) => handleDragStart(e, index) : undefined}
        onDragOver={interactive ? (e) => handleDragOver(e, index) : undefined}
        onDragLeave={interactive ? handleDragLeave : undefined}
        onDrop={interactive ? (e) => handleDrop(e, index) : undefined}
        onDragEnd={interactive ? handleDragEnd : undefined}
        onPointerDown={interactive && dragEnabled ? (e) => handlePointerDown(e, index) : undefined}
        className={`
          w-full flex items-center gap-2 rounded-lg border-2 font-medium select-none
          transition-transform duration-100
          ${interactive && dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isDragging ? 'opacity-40' : ''}
          ${isDragOver ? 'scale-[1.02] ring-2 ring-brand-primary' : ''}
        `}
        style={{
          backgroundColor: bg,
          color,
          borderColor,
          padding: '8px 14px',
          fontSize: `${fontSize}px`,
          fontFamily,
          touchAction: interactive && dragEnabled ? 'none' : undefined,
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      >
        {showNumbers && (
          <span
            className="inline-flex items-center justify-center font-semibold flex-shrink-0"
            style={{
              color: numberStyleColor,
              fontSize: `${fontSize}px`,
              fontFamily,
              opacity: numberStyleOpacity,
              minWidth: `${Math.max(fontSize + 8, 24)}px`,
            }}
          >
            {index + 1}.
          </span>
        )}
        <span className="flex-1 whitespace-pre-wrap break-words">{tile.text}</span>
      </div>
    );
  };

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        {hint && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Lightbulb size={14} className="text-amber-500" />
              {hint}
            </p>
          </div>
        )}

        <div ref={scrollRef} className={`flex-1 min-h-0 p-3 flex flex-col ${alignItemsClass} justify-center gap-2 overflow-auto`}>
          {scrambledTiles.length === 0 ? (
            <p className="text-sm text-gray-400 text-center">
              {t('games.orderSentences.enterSentencesInSettings')}
            </p>
          ) : (
            scrambledTiles.map((tile, idx) => renderTileRow(tile, idx, false))
          )}
        </div>
      </div>
    );
  }

  // ---- INTERACTIVE ----
  return (
    <div
      className="w-full h-full flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor: 'transparent' }}
    >
      {hint && (
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Lightbulb size={14} className="text-amber-500" />
            {hint}
          </p>
        </div>
      )}

      <div className={`flex-1 min-h-0 p-3 flex flex-col ${alignItemsClass} justify-center gap-2 overflow-auto`}>
        {tiles.map((tile, idx) => renderTileRow(tile, idx, true))}
      </div>

      {!showResult && !hideCheckButton && (
        <div
          className={`px-3 py-2 flex items-center justify-end gap-2 ${
            isActionBarTransparent ? '' : 'border-t border-gray-100'
          }`}
          style={{ backgroundColor: actionBarColor }}
        >
          <button
            onClick={handleCheck}
            disabled={!canCheck}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
              canCheck
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {t('games.common.check')}
          </button>
        </div>
      )}
    </div>
  );
};

// Validate a sentence-order arrangement. `arrangement` is Tile[] with
// `originalIndex` set. Returns per-position results + overall correctness.
export const validateOrderSentences = (content, arrangement = []) => {
  const sentences = getOrderSentencesFromContent(content);
  const tiles = Array.isArray(arrangement) ? arrangement : [];
  const perItemResults = sentences.map((_, i) => {
    const tile = tiles[i];
    return !!tile && tile.originalIndex === i;
  });
  const totalItems = sentences.length;
  const correctItems = perItemResults.filter(Boolean).length;
  const isCorrect = totalItems > 0 && correctItems === totalItems;
  return { perItemResults, isCorrect, correctItems, totalItems };
};

export default OrderSentencesElement;
